import type { Option } from "@tento-lona/sheets";
import { DateTime } from "@tento-chrono";
import { Field, Model, Scalar } from "@tento-core/model";
import { Json, type JsonObject, WireDecode } from "@tento-lona/sheets";
import type { PreferencesStorage } from "../../preferences/preferences-storage.ts";
import type {
  ChatContentBlock,
  ChatMessage,
  PipelineDebug,
} from "@tento-chat/model/chat-types";

const SCHEMA_VERSION = 1 as const;
const INDEX_KEY = "chat/index";
const LAST_ACTIVE_KEY = "chat/last-active-session";
const SESSION_PREFIX = "chat/session/";

const DEFAULT_MAX_SESSIONS = 50;
const DEFAULT_MAX_DEBUG_ENTRIES = 20;

/**
 * A chat session's id: minted by `ChatsClient.newSession` (`mint()`, a
 * UUID) or named by its caller, and read back from the store. A `string` at
 * runtime (it keys the stored session), but not any string: a message id or
 * a title is not accepted where a session is named.
 */
export type ChatSessionId = string & { readonly __brand: "ChatSessionId" };

/** A chat session's id (`parse` / `of` / `mint`). */
export namespace ChatSessionId {
  /** `raw` as a `ChatSessionId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<ChatSessionId> {
    return raw.length > 0 ? (raw as ChatSessionId) : null;
  }

  /** `raw` as a `ChatSessionId`; throws when `parse` refuses it. */
  export function of(raw: string): ChatSessionId {
    const id = parse(raw);
    if (id == null) throw new Error("ChatSessionId.of: not a ChatSessionId: " + JSON.stringify(raw));
    return id;
  }

  /** A fresh session id (a random UUID). */
  export function mint(): ChatSessionId {
    return of(crypto.randomUUID());
  }
}

/**
 * The provider a chat session talks to, by the name the chat models route
 * lists it under (`"claude"`, `"debug"`). The routes decide the set, so this
 * is a checked name rather than a closed union.
 */
export type ChatProvider = string & { readonly __brand: "ChatProvider" };

/** A chat provider's name (`parse` / `of`). */
export namespace ChatProvider {
  /** `raw` as a `ChatProvider`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<ChatProvider> {
    return raw.length > 0 ? (raw as ChatProvider) : null;
  }

  /** `raw` as a `ChatProvider`; throws when `parse` refuses it. */
  export function of(raw: string): ChatProvider {
    const provider = parse(raw);
    if (provider == null) throw new Error("ChatProvider.of: not a ChatProvider: " + JSON.stringify(raw));
    return provider;
  }
}

/** A chat message's pipeline debug record, kept beside its session. */
export type ChatDebugEntry = {
  messageId: string;
  debug: PipelineDebug;
};

/** A stored chat session: its messages, title, timestamps and debug entries. */
export type ChatSession = {
  schemaVersion: 1;
  id: ChatSessionId;
  title: Option<string>;
  createdAt: number;
  updatedAt: number;
  provider: ChatProvider;
  model: string;
  messages: ChatMessage[];
  debugEntries: ChatDebugEntry[];
};

/** A stored chat session as a list shows it: its id, title and last update. */
export type ChatSessionSummary = {
  id: ChatSessionId;
  title: Option<string>;
  updatedAt: number;
};

type StoredIndex = {
  schemaVersion: 1;
  sessions: ChatSessionSummary[];
};

/**
 * The local store of chat sessions — messages, per-turn `PipelineDebug`
 * entries, and the last-active session pointer — kept synchronously in a
 * `PreferencesStorage` (`localStorage` in the app, `MemoryStorage` in
 * tests); nothing is fetched, so its reads are `peek*`. An index blob caches
 * summaries so the UI can list recent conversations without parsing every
 * session body. `ChatsClient` writes its sessions through one.
 */
export class ChatSessionStore {
  readonly #storage: PreferencesStorage;
  readonly #maxSessions: number;
  readonly #maxDebugEntriesPerSession: number;

  /** Persist into `storage`; `opts` `null` (or an unset field) takes the

   *  default caps. */
  constructor(
    storage: PreferencesStorage,
    opts: Option<ChatSessionStore.Options>,
  ) {

    this.#storage = storage;
    this.#maxSessions = opts?.maxSessions ?? DEFAULT_MAX_SESSIONS;
    this.#maxDebugEntriesPerSession =
      opts?.maxDebugEntriesPerSession ?? DEFAULT_MAX_DEBUG_ENTRIES;
  }

  // ── Summaries / index ────────────────────────────────────────────

  /** Most-recent-first session summaries; `null` limit lists them all. */
  peekSessions(limit: Option<number>): ChatSessionSummary[] {
    const idx = this.#readIndex();
    const sorted = [...idx.sessions].sort((a, b) => b.updatedAt - a.updatedAt);
    return limit == null ? sorted : sorted.slice(0, limit);
  }

  // ── Full session CRUD ────────────────────────────────────────────

  peek(id: ChatSessionId): Option<ChatSession> {
    const raw = this.#storage.getItem(ChatSessionStore.sessionKey(id));
    if (raw == null) return null;
    const session = ChatSessionStore.parseSession(raw);
    if (session == null) {
      // Corrupt or schema mismatch — purge and fall through.
      this.#storage.removeItem(ChatSessionStore.sessionKey(id));
      return null;
    }
    return session;
  }

  save(session: ChatSession): void {
    const trimmed = trimDebugEntries(session, this.#maxDebugEntriesPerSession);
    this.#writeSessionWithQuotaRetry(trimmed);
    this.#upsertIndexEntry({
      id: trimmed.id,
      title: trimmed.title,
      updatedAt: trimmed.updatedAt,
    });
    this.#enforceMaxSessions(trimmed.id);
  }

  delete(id: ChatSessionId): void {
    this.#storage.removeItem(ChatSessionStore.sessionKey(id));
    const idx = this.#readIndex();
    idx.sessions = idx.sessions.filter((s) => s.id !== id);
    this.#writeIndex(idx);
    if (this.peekLastActiveSessionId() === id) {
      this.setLastActiveSessionId(null);
    }
  }

  // ── Last-active pointer ──────────────────────────────────────────

  peekLastActiveSessionId(): Option<ChatSessionId> {
    const raw = this.#storage.getItem(LAST_ACTIVE_KEY);
    return raw == null ? null : ChatSessionId.parse(raw);
  }

  setLastActiveSessionId(id: Option<ChatSessionId>): void {
    if (id == null) {
      this.#storage.removeItem(LAST_ACTIVE_KEY);
    } else {
      this.#storage.setItem(LAST_ACTIVE_KEY, id);
    }
  }

  // ── Internals ────────────────────────────────────────────────────

  #readIndex(): StoredIndex {
    const raw = this.#storage.getItem(INDEX_KEY);
    if (raw == null) return { schemaVersion: SCHEMA_VERSION, sessions: [] };
    try {
      const result = STORED_INDEX.safeHydrate(Json.wire(JSON.parse(raw)) ?? null);
      if (result.isOk) return result.value;
    } catch {
      /* fall through */
    }
    // Corrupt or versioned-out index — start fresh.
    return { schemaVersion: SCHEMA_VERSION, sessions: [] };
  }

  #writeIndex(idx: StoredIndex): void {
    this.#storage.setItem(INDEX_KEY, JSON.stringify(idx));
  }

  #upsertIndexEntry(entry: ChatSessionSummary): void {
    const idx = this.#readIndex();
    const filtered = idx.sessions.filter((s) => s.id !== entry.id);
    filtered.push(entry);
    this.#writeIndex({ schemaVersion: SCHEMA_VERSION, sessions: filtered });
  }

  #enforceMaxSessions(keepId: ChatSessionId): void {
    const idx = this.#readIndex();
    if (idx.sessions.length <= this.#maxSessions) return;
    const sorted = [...idx.sessions].sort((a, b) => a.updatedAt - b.updatedAt);
    while (sorted.length > this.#maxSessions) {
      const victim = sorted.shift();
      if (!victim || victim.id === keepId) continue;
      this.#storage.removeItem(ChatSessionStore.sessionKey(victim.id));
    }
    this.#writeIndex({ schemaVersion: SCHEMA_VERSION, sessions: sorted });
  }

  #writeSessionWithQuotaRetry(session: ChatSession): void {
    try {
      this.#storage.setItem(ChatSessionStore.sessionKey(session.id), ChatSessionStore.serializeSession(session));
      return;
    } catch (err) {
      if (!isQuotaExceeded(err)) throw err;
    }
    // Evict the oldest session other than this one, then retry once.
    const idx = this.#readIndex();
    const victim = [...idx.sessions]
      .filter((s) => s.id !== session.id)
      .sort((a, b) => a.updatedAt - b.updatedAt)[0];
    if (victim) {
      this.#storage.removeItem(ChatSessionStore.sessionKey(victim.id));
      idx.sessions = idx.sessions.filter((s) => s.id !== victim.id);
      this.#writeIndex(idx);
    }
    this.#storage.setItem(ChatSessionStore.sessionKey(session.id), ChatSessionStore.serializeSession(session));
  }
}

/**
 * Types and values that go with `ChatSessionStore`: `Options`, `sessionKey`,
 * `serializeSession`, `parseSession`.
 */
export namespace ChatSessionStore {
  /**
   * The store's eviction limits: `null` takes the default (50 sessions, 20
   * debug entries per session).
   */
  export type Options = {
    readonly maxSessions: Option<number>;
    readonly maxDebugEntriesPerSession: Option<number>;
  };

  export function sessionKey(id: ChatSessionId): string {
    return SESSION_PREFIX + id;
  }

  export function serializeSession(session: ChatSession): string {
    // DateTime instances provide a `toJSON` method that returns RFC3339,
    // so plain JSON.stringify handles them correctly.
    return JSON.stringify(session);
  }

  /**
   * A stored session decoded through `SESSION` (see below): `null` when the
   * text is not JSON, is another schema version, or a session field has the
   * wrong type. A message or debug entry that does not decode is dropped
   * (the rest of the conversation survives one corrupt turn).
   */
  export function parseSession(raw: string): Option<ChatSession> {
    let parsed: Json;
    try {
      parsed = Json.wire(JSON.parse(raw)) ?? null;
    } catch {
      return null;
    }
    const result = SESSION.safeHydrate(parsed);
    return result.isOk ? result.value : null;
  }
}

// ── Stored-session decoder ──────────────────────────────────────────

/** A value that must equal `SCHEMA_VERSION`. */
const SCHEMA: Model<typeof SCHEMA_VERSION> = Model.scalar(
  `schema version ${SCHEMA_VERSION}`,
  (raw) => {
    if (raw !== SCHEMA_VERSION) throw new Error("unsupported schema version");
    return SCHEMA_VERSION;
  },
  null,
);

/** An RFC 3339 string revived as a `DateTime` (what `DateTime.toJSON` writes). */
const DATE_TIME: Model<DateTime> = Scalar.checked<string, DateTime>(
  "DateTime",
  Scalar.string,
  (raw) => {
    const parsed = DateTime.fromRfc3339(raw);
    return parsed.isErr ? null : parsed.exp();
  },
  null,
);

/**
 * A content block: an object with a string `type`. The block union is open
 * (hosts add their own block types), so the block's other fields pass
 * through as stored; the renderer dispatches on `type`.
 */
export const CONTENT_BLOCK: Model<ChatContentBlock> = Scalar.checked<BlockEnvelope, ChatContentBlock>(
  "ChatContentBlock",
  Model.object<BlockEnvelope>({ type: Field.required(Scalar.string) }),
  // The open union past its checked envelope: kept as stored.
  (_envelope, raw) => raw as ChatContentBlock,
  null,
);

/** What a content block's checked envelope holds. */
interface BlockEnvelope {
  readonly type: string;
}

/**
 * A turn's pipeline debug record: its envelope is checked (`type`, the
 * session and count fields, a `nodes` list); `request` and the node records
 * are debug payloads kept as stored.
 */
export const PIPELINE_DEBUG: Model<PipelineDebug> = Scalar.checked<DebugEnvelope, PipelineDebug>(
  "PipelineDebug",
  Model.object<DebugEnvelope>({
    type: Field.required(Scalar.stringUnion(["pipeline_debug"] as const)),
    sessionId: Field.required(Scalar.string),
    turnCount: Field.required(Scalar.number),
    knowledgeCount: Field.required(Scalar.number),
    systemInstruction: Field.optional(Scalar.string),
    nodes: Field.required(Model.array(Model.object({}))),
  }),
  // The debug payloads past the checked envelope: kept as stored.
  (_envelope, raw) => raw as PipelineDebug,
  null,
);

/** What a pipeline debug record's checked envelope holds. */
interface DebugEnvelope {
  readonly type: "pipeline_debug";
  readonly sessionId: string;
  readonly turnCount: number;
  readonly knowledgeCount: number;
  readonly systemInstruction?: string;
  readonly nodes: readonly JsonObject[];
}

const MESSAGE: Model<ChatMessage> = Model.object<ChatMessage>({
  id: Field.required(Scalar.string),
  role: Field.required(Scalar.stringUnion(["user", "assistant", "system"] as const)),
  content: Field.required(Model.array(CONTENT_BLOCK)),
  isStreaming: Field.required(Scalar.boolean),
  createdAt: Field.required(Scalar.number),
  streamStartedAt: WireDecode.omittable(DATE_TIME),
  streamEndedAt: WireDecode.omittable(DATE_TIME),
});

const DEBUG_ENTRY: Model<ChatDebugEntry> = Model.object<ChatDebugEntry>({
  messageId: Field.required(Scalar.string),
  debug: Field.required(PIPELINE_DEBUG),
});

/** A list whose items decode through `item`; an item that does not is dropped. */
function survivors<T>(item: Model<T>): Model<T[]> {
  return Model.scalar<T[]>(
    "array",
    (raw) => {
      if (!Array.isArray(raw)) throw new Error("expected array");
      const out: T[] = [];
      for (const entry of raw) {
        const result = item.safeHydrate(entry);
        if (result.isOk) out.push(result.value);
      }
      return out;
    },
    null,
  );
}

/** A stored session id: checked (`ChatSessionId.parse`), so an empty one does not decode. */
const SESSION_ID: Model<ChatSessionId> = Scalar.checked<string, ChatSessionId>(
  "ChatSessionId",
  Scalar.string,
  ChatSessionId.parse,
  null,
);

/** A session index entry; one that does not decode is dropped from the index. */
const SESSION_SUMMARY: Model<ChatSessionSummary> = Model.object<ChatSessionSummary>({
  id: Field.required(SESSION_ID),
  title: WireDecode.nullable(Scalar.string),
  updatedAt: Field.required(Scalar.number),
});

/** The stored session index: this schema version's, with its decodable entries. */
const STORED_INDEX: Model<StoredIndex> = Model.object<StoredIndex>({
  schemaVersion: Field.required(SCHEMA),
  sessions: Field.required(survivors(SESSION_SUMMARY)),
});

const SESSION: Model<ChatSession> = Model.object<ChatSession>({
  schemaVersion: Field.required(SCHEMA),
  id: Field.required(SESSION_ID),
  title: WireDecode.nullable(Scalar.string),
  createdAt: Field.required(Scalar.number),
  updatedAt: Field.required(Scalar.number),
  provider: Field.required(Scalar.checked<string, ChatProvider>("ChatProvider", Scalar.string, ChatProvider.parse, null)),
  model: Field.required(Scalar.string),
  messages: Field.required(survivors(MESSAGE)),
  debugEntries: Field.required(survivors(DEBUG_ENTRY)),
});

function isQuotaExceeded(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const name = (err as { name?: string }).name;
  return (
    name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED"
  );
}

function trimDebugEntries(
  session: ChatSession,
  max: number,
): ChatSession {
  if (session.debugEntries.length <= max) return session;
  // Keep newest (end of array in our convention).
  return {
    ...session,
    debugEntries: session.debugEntries.slice(-max),
  };
}


